<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table">
				<thead class="sticky-header">
					<tr>
						<th class="icon-col"></th>
						<SortHeader
							title="name"
							:asc="currentSort.asc"
							:sort-key="currentSort.key"
							@click="() => setSort('name')"
							data-cy="report-table-header"
							class="header-cell"
							color="#695b59"
							weight="500"
						></SortHeader>
						<th>description</th>
						<th>student id</th>
						<th v-if="schoolStore.schoolDetails.data?.integration !== 'none'">
							<span class="d-flex align-center justify-content-center">
								Link household
								<v-checkbox
									data-cy="students-link-household-toggle-all"
									:model-value="headerLinkHouseholdChecked"
									@update:model-value="toggleAllLinkHousehold"
									:disabled="deleted || !canEditDirectory"
									hide-details
									style="
										max-width: max-content;
										max-height: 24px;
										overflow: hidden;
										display: flex;
										align-items: center;
									"
									class="custom-input"
									density="compact"
								></v-checkbox>
							</span>
						</th>
						<th class="icon-col">
							<v-btn
								@click="studentsS.students.reload()"
								icon
								color="gray200"
								size="x-small"
								:loading="studentsS.students.loading"
								style="max-width: 20px; max-height: 20px"
							>
								<v-icon>mdi-reload</v-icon></v-btn
							>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="row in paginatedResults"
						:key="row.uid"
						:class="{
							'table-row': true,
							selected:
								(editModal.show && editModal.student === row) ||
								(editRelationModal.show && editRelationModal.student === row),
						}"
					>
						<td>
							<v-btn
								@click="updatePinUser(row)"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:loading="state.loadPinned.includes(row.uid)"
								:disabled="deleted || !canEditDirectory"
							>
								<v-tooltip location="bottom" class="custom-tooltip" activator="parent">
									Lock Record
								</v-tooltip>
								<v-icon :color="row.pinned ? 'primary' : 'gray500'"
									>mdi-lock-outline</v-icon
								>
							</v-btn>
						</td>
						<td data-cy="table-col-name">
							{{ row.child_fname }} {{ row.child_lname }}
							<v-btn
								@click="
									editModal.show = true;
									editModal.student = row;
								"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:disabled="!canEditDirectory"
							>
								<v-icon color="gray500">mdi-note-edit</v-icon>
							</v-btn>
						</td>
						<td data-cy="table-col-description">
							{{ row.description }}
						</td>
						<td data-cy="table-col-external-id">
							{{ row.external_child_id || row.external_id }}
						</td>
						<td data-cy="table-col-linkhousehold" v-if="schoolStore.schoolDetails.data?.integration !== 'none'">
							<span class="d-flex align-center justify-content-center">
								<v-checkbox
									:model-value="displayLinkHouseholdForRow(row)"
									@update:model-value="onRowLinkHouseholdUpdate(row, $event)"
									:loading="state.loadLinkHousehold.includes(row.uid)"
									:disabled="deleted || !canEditDirectory"
									hide-details
									density="compact"
								></v-checkbox></span>
						</td>
						<td>
							<v-btn
								@click="
									editRelationModal.show = true;
									editRelationModal.student = row;
								"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:disabled="deleted || !canEditDirectory"
							>
								<v-icon color="gray500">mdi-account-group</v-icon>
							</v-btn>
						</td>
					</tr>
				</tbody>
			</table>

			<div
				v-if="studentsS.students.loading && !studentsS.students.data.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<LoadingState text="Loading Students"></LoadingState>
			</div>
			<div
				v-if="!studentsS.students.loading && !studentsS.students.data.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">We couldn't find any Students.</div>
					<p>Try reloading the page or adding a new student.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination v-bind="forComponent()"></pagination>
		<div class="table-buttons" v-if="canEditDirectory">
			<v-btn
				v-if="showLinkHouseholdSave"
				data-cy="students-link-household-save-btn"
				:loading="state.saveLinkHouseholdBulk"
				:disabled="deleted"
				color="primary"
				@click="saveLinkHouseholdBulk"
			>
				{{ $t("message.StudentsDirectory.saveLinkHousehold") }}
			</v-btn>
			<v-btn @click="addModal.show = true" data-cy="directory-student-create-btn">Create New</v-btn>
			<v-btn
				@click="uploadModal.show = true"
				color="#E5E4DB"
				append-icon="mdi-tray-arrow-up"
				>Upload</v-btn
			>

			<div class="d-flex align-center">
				<v-btn
					@click="download"
					:loading="state.loadDownload"
					color="#E5E4DB"
					append-icon="mdi-tray-arrow-down"
					>Download</v-btn
				>
				<div class="ml-1" tabindex="1">
					<v-tooltip location="bottom" activator="parent">Download Relations</v-tooltip>
					<RelationsDownload
						:download-types="['enrollment', 'team_enrollment', 'relationship']"
					></RelationsDownload>
				</div>
			</div>
		</div>
	</div>
	<edit-student-modal
		v-model:show="editModal.show"
		:student="editModal.student"
	></edit-student-modal>
	<add-student-modal
		v-model:show="addModal.show"
		@update:search="
			$emit('update:search', $event);
			addModal.show = false;
		"
	></add-student-modal>
	<student-relations-modal
		v-model:show="editRelationModal.show"
		:directory-entity="editRelationModal.student"
		:entity-type="'student'"
		:entity-id="editRelationModal.student.uid"
		:entity-name="`${editRelationModal.student.child_fname} ${editRelationModal.student.child_lname}`"
		:tabs="['parent', 'teacher', 'team', 'class']"
		@entity-click="handleEntityClick"
	></student-relations-modal>

	<upload-csv-modal v-model:show="uploadModal.show" :tab="props.tab" />
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import usePaginate from "@/hooks/usePaginate";
import pagination from "@/components/pagination.vue";
import downloadFile, { complexSearch } from "@/utils";
import type { StudentsType } from "@/api_types/school";
import EditStudentModal from "./editStudentModal.vue";
import AddStudentModal from "./addStudentModal.vue";
import useDirectoryNavigation from "@/stores/school/directoryNavigation";
import StudentRelationsModal from "@/components/school/directory/RelationshipModal.vue";
import useStudents from "@/stores/school/students";
import { useAlertsStore } from "@/stores/alerts";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import uploadCsvModal from "../uploadCsvModal.vue";
import FetchService from "@/services/fetch.service";
import RelationsDownload from "../relationsDownload.vue";
import SortHeader from "@/components/global/sortHeader.vue";
import useTableSort from "@/hooks/useTableSort";
import { sort } from "@/utils/sort";
import { useSchoolStore } from "@/stores/school";
import { useAuthenticationStore } from "@/stores/authentication";

const props = defineProps<{
	tab: "teacher" | "parent" | "student" | "team";
	search: string;
	deleted: boolean;
	selectedEntity?: any;
}>();
const emit = defineEmits<{
  (e: 'update:search', ...args: any[]): void;
}>();

const { t } = useI18n();
const schoolStore = useSchoolStore();
const authStore = useAuthenticationStore();
const canEditDirectory = computed(() => authStore.hasPolicy("school.directory.student.write"));

const { currentSort, setSort } = useTableSort([
	{
		key: "name",
		title: "name",
		sortKey: "name",
	},
]);

const state = reactive({
	loadPinned: [] as string[],
	loadDownload: false,
	loadLinkHousehold: [] as string[],
	saveLinkHouseholdBulk: false,
});

const editModal = reactive({ show: false, student: {} as StudentsType[number] });
const addModal = reactive({ show: false });
const editRelationModal = reactive({ show: false, student: {} as StudentsType[number] });

const uploadModal = reactive({
	show: false,
});
const studentsS = useStudents();
const directoryNav = useDirectoryNavigation();
studentsS.students.reload();
const filteredData = computed(() => {
	return sort(
		studentsS.students.data?.filter((i) => {
			if (i.deleted !== props.deleted) return false;
			return complexSearch(
				props.search || "",
				`${i.child_fname} ${i.child_lname} ${i.external_id} ${i.external_child_id}`
			);
		}) || [],
		(i) => `${i.child_lname} ${i.child_fname}`,
		currentSort.value.asc
	);
});

/**
 * Local-only "select all": one ref, no mass mutations. Rows here are always from filteredData
 * (via paginate). Clear bulk overlay when filter/sort/search/deleted-tab changes — not when
 * students list refetches, or a pending bulk edit can vanish before save (slow/prod API + CI).
 */
const linkHouseholdBulkValue = ref<boolean | null>(null);

watch(
	() => [
		props.search,
		props.deleted,
		currentSort.value.asc,
		currentSort.value.key,
	],
	() => {
		linkHouseholdBulkValue.value = null;
	}
);

const displayLinkHouseholdForRow = (row: StudentsType[number]): boolean => {
	const bulk = linkHouseholdBulkValue.value;
	return bulk !== null ? bulk : !!row.link_household;
};

const headerLinkHouseholdChecked = computed(() => {
	const bulk = linkHouseholdBulkValue.value;
	if (bulk !== null) {
		return bulk;
	}
	const list = filteredData.value;
	return list.length > 0 && list.every((s) => !!s.link_household);
});

const toggleAllLinkHousehold = (value: boolean | null) => {
	linkHouseholdBulkValue.value = !!value;
};

const showLinkHouseholdSave = computed(
	() =>
		linkHouseholdBulkValue.value !== null &&
		schoolStore.schoolDetails.data?.integration !== "none"
);

const saveLinkHouseholdBulk = async () => {
	const target = linkHouseholdBulkValue.value;
	if (target === null || props.deleted) {
		return;
	}
	const alertsStore = useAlertsStore();
	state.saveLinkHouseholdBulk = true;
	const list = filteredData.value;
	const uids = list
		.filter((s) => !!s.link_household !== target)
		.map((s) => s.uid);
	try {
		linkHouseholdBulkValue.value = null;
		if (uids.length === 0) {
			alertsStore.addAlert(
				"info",
				t("message.StudentsDirectory.saveLinkHouseholdNothing"),
				5
			);
			return;
		}
		const ok = await studentsS.bulkSetLinkHouseholdForFilter(list, target);
		if (ok) {
			alertsStore.addAlert(
				"success",
				t("message.StudentsDirectory.saveLinkHouseholdSuccess", { count: uids.length }),
				5
			);
		} else {
			alertsStore.addAlert(
				"error",
				t("message.StudentsDirectory.saveLinkHouseholdError"),
				10
			);
		}
	} catch {
		alertsStore.addAlert("error", t("message.StudentsDirectory.saveLinkHouseholdError"), 10);
	} finally {
		state.saveLinkHouseholdBulk = false;
		studentsS.students.reload();
	}
};

const updatePinUser = async (student: StudentsType[number]) => {
	state.loadPinned.push(student.uid);
	const res = await studentsS.updateStudent(student.uid, {
		pinned: !student.pinned,
	});
	useAlertsStore().addAlert(
		res ? "success" : "error",
		res ? "Student saved successfully" : "Failed to update student",
		5
	);
	state.loadPinned = state.loadPinned.filter((str) => str !== student.uid);
};

const updateLinkHousehold = async (student: StudentsType[number], value: boolean) => {
	state.loadLinkHousehold.push(student.uid);
	const res = await studentsS.updateStudent(student.uid, {
		link_household: value,
	});
	useAlertsStore().addAlert(
		res ? "success" : "error",
		res ? "Student saved successfully" : "Failed to update student",
		5
	);
	state.loadLinkHousehold = state.loadLinkHousehold.filter((str) => str !== student.uid);
};

const onRowLinkHouseholdUpdate = (row: StudentsType[number], value: boolean | null) => {
	linkHouseholdBulkValue.value = null;
	void updateLinkHousehold(row, !!value);
};

const { forComponent, paginatedResults } = usePaginate(filteredData);

const download = async () => {
	state.loadDownload = true;
	const res = await FetchService.fetchData("/students/download", "get", undefined, true);
	const cType = res?.res?.headers.get?.("content-type") || "";
	if (typeof res?.data === "string" && cType.includes("text/csv")) {
		downloadFile(
			res.data,
			res?.res?.headers
				?.get("content-disposition")
				?.split("filename=")?.[1]
				?.replace(/"/g, "") || "students.csv",
			"text/csv"
		);
	} else {
		useAlertsStore().addAlert(
			"error",
			"Failed to download students, please try again later",
			10
		);
	}
	state.loadDownload = false;
};

// Handle entity clicks from relationship modal
const handleEntityClick = (entityData: any) => {
	// Navigate to the entity using the store
	directoryNav.navigateToEntity(entityData);
};

// Watch for selected entity changes and open the relationship modal
watch(() => directoryNav.selectedEntity, (newEntity) => {
	if (newEntity && newEntity.entityType === 'student') {
		// Find the student in the current data
		const student = studentsS.students.data.find(s => s.uid === newEntity.entityId);
		if (student) {
			editRelationModal.student = student;
			editRelationModal.show = true;
			// Clear the selected entity after opening the modal
			directoryNav.clearSelectedEntity();
		}
	}
}, { immediate: true });
</script>
<style scoped lang="scss">
.icon-col {
	width: 0px;
}
.table-row {
	td {
		transition: background-color 0.3s;
	}
	&.selected {
		td {
			background-color: #e6e6e689 !important;
		}
	}
}
</style>
